import type {
  ConfidenceScoreEntry,
  ConfidenceScoresMap,
} from '../../services/api/confidenceScoreApi'
import type { RealtimeChannel } from '@supabase/supabase-js'
import { supabase } from '../../utils/supabase'
import { fetchConfidenceScores } from '../../services/api/confidenceScoreApi'
import { fetchScorePending } from '../../services/api/scorePendingApi'

// ── Types ─────────────────────────────────────────────────────────────────────

export type { ConfidenceScoreEntry }

// ── Module-level state ────────────────────────────────────────────────────────

/** mindmapId → nodeId → ScoreEntry */
const store = new Map<string, ConfidenceScoresMap>()

/** mindmapId → fetch state */
type FetchState = 'fetching' | 'loaded'
const fetchStateMap = new Map<string, FetchState>()

/** Per-mindmap backoff: timestamp of last fetch failure (ms) */
const failureTimestampMap = new Map<string, number>()
const FETCH_BACKOFF_MS = 30_000

/**
 * Per-mindmap generation counter.
 * Incremented on every invalidation so that in-flight fetches from before the
 * invalidation can detect they are stale and discard their results.
 */
const generationMap = new Map<string, number>()

/**
 * Nodes currently being scored — shown as loading badges in the UI.
 * mindmapId → Set of nodeIds
 */
const pendingNodesMap = new Map<string, Set<string>>()

/**
 * Auto-timeout handles per mindmap — clears pending state after 2 minutes
 * as a safety fallback if the score_updated broadcast is never received.
 */
const pendingTimeoutMap = new Map<string, ReturnType<typeof setTimeout>>()
const SCORING_TIMEOUT_MS = 120_000

/**
 * Ref-counted channel registry — one Supabase channel per mindmapId.
 * Tracks subscriber count so the channel is removed when the last node unmounts.
 */
interface ChannelEntry {
  channel: RealtimeChannel
  refCount: number
}
const channelRegistry = new Map<string, ChannelEntry>()

/** Pending debounce timers for score refresh — one per mindmapId */
const refreshTimerMap = new Map<string, ReturnType<typeof setTimeout>>()

/** Change listeners — notified on any store mutation */
const listeners = new Set<() => void>()

// ── Internal helpers ──────────────────────────────────────────────────────────

function notify(): void {
  for (const cb of listeners) cb()
}

function currentGeneration(mindmapId: string): number {
  return generationMap.get(mindmapId) ?? 0
}

function incrementGeneration(mindmapId: string): number {
  const next = currentGeneration(mindmapId) + 1
  generationMap.set(mindmapId, next)
  return next
}

/** Mark nodeIds as pending (scoring in progress). Resets the auto-clear timeout. */
function markNodesPending(mindmapId: string, nodeIds: string[]): void {
  if (nodeIds.length === 0) return
  const set = pendingNodesMap.get(mindmapId) ?? new Set<string>()
  for (const id of nodeIds) set.add(id)
  pendingNodesMap.set(mindmapId, set)

  // Reset the safety timeout — if we never hear back, clear after 2 min
  const existing = pendingTimeoutMap.get(mindmapId)
  if (existing !== undefined) clearTimeout(existing)
  const timer = setTimeout(() => {
    pendingTimeoutMap.delete(mindmapId)
    pendingNodesMap.delete(mindmapId)
    notify()
  }, SCORING_TIMEOUT_MS)
  pendingTimeoutMap.set(mindmapId, timer)

  notify()
}

/**
 * Replace the entire pending set for a mindmap with the given list. Used to
 * reconcile local state against the DB on every store load — ensures that
 * dropped broadcasts (e.g. a missed score_updated) self-heal on the next
 * refresh instead of leaving a badge stuck in loading until the 2-min in-
 * memory timeout fires.
 */
function setPendingNodeIds(mindmapId: string, nodeIds: string[]): void {
  if (nodeIds.length === 0) {
    if (!pendingNodesMap.has(mindmapId)) return
    pendingNodesMap.delete(mindmapId)
    const timer = pendingTimeoutMap.get(mindmapId)
    if (timer !== undefined) {
      clearTimeout(timer)
      pendingTimeoutMap.delete(mindmapId)
    }
    notify()
    return
  }

  pendingNodesMap.set(mindmapId, new Set(nodeIds))

  const existing = pendingTimeoutMap.get(mindmapId)
  if (existing !== undefined) clearTimeout(existing)
  const timer = setTimeout(() => {
    pendingTimeoutMap.delete(mindmapId)
    pendingNodesMap.delete(mindmapId)
    notify()
  }, SCORING_TIMEOUT_MS)
  pendingTimeoutMap.set(mindmapId, timer)

  notify()
}

/** Clear a single node from pending state when its score_updated broadcast arrives. */
function clearNodePending(mindmapId: string, nodeId: string): void {
  const set = pendingNodesMap.get(mindmapId)
  if (!set) return
  set.delete(nodeId)
  if (set.size === 0) {
    pendingNodesMap.delete(mindmapId)
    const timer = pendingTimeoutMap.get(mindmapId)
    if (timer !== undefined) {
      clearTimeout(timer)
      pendingTimeoutMap.delete(mindmapId)
    }
  }
  notify()
}

// ── Public API ────────────────────────────────────────────────────────────────

export function getScoreSnapshot(
  nodeId: string,
  mindmapId: string
): ConfidenceScoreEntry | undefined {
  return store.get(mindmapId)?.[nodeId]
}

export function isNodePending(nodeId: string, mindmapId: string): boolean {
  return pendingNodesMap.get(mindmapId)?.has(nodeId) ?? false
}

export function subscribeToScoresStore(cb: () => void): () => void {
  listeners.add(cb)
  return () => listeners.delete(cb)
}

export function setScores(mindmapId: string, scores: ConfidenceScoresMap): void {
  store.set(mindmapId, scores)
  fetchStateMap.set(mindmapId, 'loaded')
  notify()
}

/** Mark a fetch as failed — clears fetching state so the next mount can retry (with backoff). */
function markFetchFailed(mindmapId: string): void {
  fetchStateMap.delete(mindmapId)
  failureTimestampMap.set(mindmapId, Date.now())
}

function invalidateMindmapScores(mindmapId: string): void {
  fetchStateMap.delete(mindmapId) // clears both 'fetching' and 'loaded'
  store.delete(mindmapId)
  incrementGeneration(mindmapId)
  notify()
}

function isScoresFetched(mindmapId: string): boolean {
  return fetchStateMap.get(mindmapId) === 'loaded'
}

function isScoresFetching(mindmapId: string): boolean {
  return fetchStateMap.get(mindmapId) === 'fetching'
}

function markFetching(mindmapId: string): void {
  fetchStateMap.set(mindmapId, 'fetching')
}

// ── Score loader ──────────────────────────────────────────────────────────────

/**
 * Idempotent fetch of all confidence scores for a mindmap.
 * Guards against duplicate in-flight requests and applies a backoff after failures.
 *
 * Also fetches the persisted "scoring in progress" set in parallel and seeds
 * the pending badge state from it — this covers the case where the user
 * triggered scoring from another page (e.g. survey close) and only just
 * navigated to the mindmap, so the realtime score_started broadcast was
 * never delivered to this client.
 *
 * @param bypassBackoff - When true (used for realtime-triggered refreshes), skips
 *   the error backoff window so a genuine score_updated broadcast is never silently dropped.
 */
export async function loadScores(mindmapId: string, bypassBackoff = false): Promise<void> {
  if (!mindmapId || isScoresFetched(mindmapId) || isScoresFetching(mindmapId)) return

  // Respect backoff window after a previous failure (unless this is a broadcast-triggered refresh)
  if (!bypassBackoff) {
    const lastFailure = failureTimestampMap.get(mindmapId)
    if (lastFailure !== undefined && Date.now() - lastFailure < FETCH_BACKOFF_MS) return
  }

  // Snapshot the generation before the async fetch so we can detect if an
  // invalidation happens while the request is in-flight.
  const snapshotGeneration = currentGeneration(mindmapId)

  markFetching(mindmapId)
  const [scores, pendingNodeIds] = await Promise.all([
    fetchConfidenceScores(mindmapId),
    fetchScorePending(mindmapId),
  ])

  // Discard result if a newer invalidation has fired since we started fetching
  if (currentGeneration(mindmapId) !== snapshotGeneration) return

  if (scores !== null) {
    setScores(mindmapId, scores)
  } else {
    // Do NOT cache as empty — allow retry after backoff
    markFetchFailed(mindmapId)
  }

  // Reconcile pending set against persisted state — independent of scores
  // success. Replaces the in-memory set entirely so a missed score_updated
  // broadcast self-heals on the next refresh. Live score_started broadcasts
  // add to this; live score_updated broadcasts remove from it.
  if (pendingNodeIds !== null) {
    setPendingNodeIds(mindmapId, pendingNodeIds)
  }
}

/**
 * Schedule a debounced refresh for a mindmap.
 * All broadcasts within a 150ms window are coalesced into one invalidate+fetch,
 * preventing N parallel GETs when a batch scoring run fires N score_updated events.
 * Bypasses the error backoff so a real score_updated event is always acted on.
 */
function scheduleRefresh(mindmapId: string): void {
  const existing = refreshTimerMap.get(mindmapId)
  if (existing !== undefined) clearTimeout(existing)

  const timer = setTimeout(() => {
    refreshTimerMap.delete(mindmapId)
    invalidateMindmapScores(mindmapId)
    void loadScores(mindmapId, true) // bypass backoff — broadcast means real data exists
  }, 150)

  refreshTimerMap.set(mindmapId, timer)
}

// ── Channel registry ──────────────────────────────────────────────────────────

/**
 * Registers a new subscriber for the mindmap's realtime channel.
 * Creates the channel on first call; increments ref count on subsequent calls.
 *
 * - score_started: immediately marks nodeIds as pending (no debounce) for instant UI feedback
 * - score_updated: clears the specific node from pending instantly, then debounces a full refresh
 *
 * Returns a cleanup function that decrements the ref count and removes the
 * channel when the last subscriber unmounts.
 */
export function acquireScoreChannel(mindmapId: string): () => void {
  const existing = channelRegistry.get(mindmapId)
  if (existing) {
    existing.refCount += 1
    return () => releaseScoreChannel(mindmapId)
  }

  const channel: RealtimeChannel = supabase
    .channel(`mindmap-scores:${mindmapId}`)
    .on('broadcast', { event: 'score_started' }, (event) => {
      // Immediately show loading indicator — no debounce needed
      const nodeIds = (event?.payload as { nodeIds?: string[] } | undefined)?.nodeIds
      if (nodeIds?.length) markNodesPending(mindmapId, nodeIds)
    })
    .on('broadcast', { event: 'score_updated' }, (event) => {
      // Clear this specific node's pending state instantly, then queue a full refresh
      const nodeId = (event?.payload as { nodeId?: string } | undefined)?.nodeId
      if (nodeId) clearNodePending(mindmapId, nodeId)
      scheduleRefresh(mindmapId)
    })
    .subscribe()

  channelRegistry.set(mindmapId, { channel, refCount: 1 })
  return () => releaseScoreChannel(mindmapId)
}

function releaseScoreChannel(mindmapId: string): void {
  const entry = channelRegistry.get(mindmapId)
  if (!entry) return
  entry.refCount -= 1
  if (entry.refCount <= 0) {
    // Cancel any pending timers so they don't fire after unmount
    const refreshTimer = refreshTimerMap.get(mindmapId)
    if (refreshTimer !== undefined) {
      clearTimeout(refreshTimer)
      refreshTimerMap.delete(mindmapId)
    }
    const pendingTimer = pendingTimeoutMap.get(mindmapId)
    if (pendingTimer !== undefined) {
      clearTimeout(pendingTimer)
      pendingTimeoutMap.delete(mindmapId)
    }
    pendingNodesMap.delete(mindmapId)
    void entry.channel.unsubscribe()
    channelRegistry.delete(mindmapId)

    // Invalidate fetch state so the next mount always re-fetches fresh scores.
    // Without this, scores computed while the user was away would never be loaded
    // (the channel missed those broadcasts, and loadScores would bail on 'loaded').
    fetchStateMap.delete(mindmapId)
    // Increment generation to discard any in-flight fetch that started before release
    incrementGeneration(mindmapId)
    // Clear failure backoff so it doesn't suppress the next mount's fetch
    failureTimestampMap.delete(mindmapId)
  }
}
